import { STPUVOAnalytix, UserInfoUVO } from '@bldsoft/stpanalytix'
import {
  AnalytixInitializeError,
  AnalytixNotInitializedError,
  AnalytixSendEventError,
} from './errors'
import type {
  AnalytixParams,
  AnalytixSendMethods,
  AnyableAnalytixUserInfo,
  SendEventParams,
} from './index.h'
import { ANALYTIX_EVENT_NAMES, debugAnalytixEvent, logger } from './lib'
import { send } from './model'

/**
 * Thin wrapper around {@link STPUVOAnalytix}: init, enable gate, userInfo, typed sends + effector `send` tap.
 */
export class Analytix implements AnalytixSendMethods {
  private instance: STPUVOAnalytix | null = null
  private info: ReturnType<typeof UserInfoUVO> = UserInfoUVO({})
  private enabled = false

  initialize(params: AnalytixParams) {
    try {
      logger.debug('initialize UVO analytix:', params.baseUrl)
      this.instance = STPUVOAnalytix.initialize(
        params.baseUrl,
        params.user,
        params.password,
        params.configAccountCode,
        params.contentProviderId,
        params.appName,
        params.appId,
        params.appReleaseVersion
      )
      this.info = UserInfoUVO(params.deviceUserInfo)
    } catch (error) {
      this.instance = null
      this.enabled = false
      throw new AnalytixInitializeError(error)
    }
  }

  enable() {
    logger.debug('enable analytix')
    this.enabled = true
  }

  disable() {
    logger.debug('disable analytix')
    this.enabled = false
  }

  set userInfo(patch: AnyableAnalytixUserInfo) {
    const prev =
      this.info && typeof this.info === 'object'
        ? { ...(this.info as Record<string, unknown>) }
        : {}
    this.info = UserInfoUVO({
      ...prev,
      ...patch,
    } as Parameters<typeof UserInfoUVO>[0])
  }

  private debug<T extends keyof AnalytixSendMethods>(
    name: T,
    params: Parameters<AnalytixSendMethods[T]>[0] = {} as Parameters<
      AnalytixSendMethods[T]
    >[0],
    userInfo: AnyableAnalytixUserInfo = {}
  ) {
    const eventName =
      ANALYTIX_EVENT_NAMES[name as keyof typeof ANALYTIX_EVENT_NAMES]
    const eventEmojis: Partial<Record<string, string>> = {
      session_start: '🚀',
      favorite_add: '❤️',
      favorite_remove: '💔',
      player_open: '📺',
      play_start: '▶️',
      play_stop: '⏸️',
      search: '🔍',
      page_open: '📄',
      player_watching: '👀',
      player_event: '🎮',
      play_error: '⚠️',
      epg_view: '📅',
      epg_category: '📂',
      epg_channel: '📡',
      epg_program: '🎬',
      live_module_view: '🔀',
      banner_impression: '👁️',
      banner_click: '👆',
      banner_dismiss: '✖️',
      sign_up: '🙋',
    }

    debugAnalytixEvent({
      eventName,
      params: (params ?? {}) as Record<string, unknown>,
      general: userInfo ?? {},
      emoji: eventEmojis[eventName],
    })
  }

  private async send<T extends keyof AnalytixSendMethods>(
    name: T,
    params: Parameters<AnalytixSendMethods[T]>[0]
  ) {
    if (!this.enabled) return true
    if (!this.instance) throw new AnalytixNotInitializedError()

    this.instance.userInfo = this.info

    this.debug(name, params, this.info)
    send({ name, params, general: this.info as Record<string, unknown> })

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result: boolean = await (this.instance as any)[name](params)
      if (!result) throw new AnalytixSendEventError(String(name), false)
      return true
    } catch (error) {
      if (error instanceof AnalytixSendEventError) throw error
      throw new AnalytixSendEventError(String(name), error)
    }
  }

  async sendPageOpenEvent(params: SendEventParams.PageOpen) {
    return this.send('sendPageOpenEvent', params)
  }

  async sendFavoritesAddEvent(params: SendEventParams.FavoritesAdd) {
    return this.send('sendFavoritesAddEvent', params)
  }

  async sendFavoritesRemoveEvent(params: SendEventParams.FavoritesRemove) {
    return this.send('sendFavoritesRemoveEvent', params)
  }

  async sendSessionStartEvent(params: SendEventParams.SessionStart) {
    return this.send('sendSessionStartEvent', params)
  }

  async sendSearchEvent(params: SendEventParams.Search) {
    return this.send('sendSearchEvent', params)
  }

  async sendPlayerOpenEvent(params: SendEventParams.PlayerOpen) {
    return this.send('sendPlayerOpenEvent', params)
  }

  async sendPlayStartEvent(params: SendEventParams.PlayStart) {
    return this.send('sendPlayStartEvent', params)
  }

  async sendPlayStopEvent(params: SendEventParams.PlayStop) {
    return this.send('sendPlayStopEvent', params)
  }

  async sendPlayerWatchingEvent(params: SendEventParams.PlayerWatching) {
    return this.send('sendPlayerWatchingEvent', params)
  }

  async sendPlayerEvent(params: SendEventParams.PlayerEvent) {
    return this.send('sendPlayerEvent', params)
  }

  async sendPlayErrorEvent(params: SendEventParams.PlayError) {
    return this.send('sendPlayErrorEvent', params)
  }

  async sendEpgViewEvent(params: SendEventParams.EpgView) {
    return this.send('sendEpgViewEvent', params)
  }

  async sendEpgCategoryEvent(params: SendEventParams.EpgCategory) {
    return this.send('sendEpgCategoryEvent', params)
  }

  async sendEpgChannelEvent(params: SendEventParams.EpgChannel) {
    return this.send('sendEpgChannelEvent', params)
  }

  async sendEpgProgramEvent(params: SendEventParams.EpgProgram) {
    return this.send('sendEpgProgramEvent', params)
  }

  async sendLiveModuleViewEvent(params: SendEventParams.LiveModuleView) {
    return this.send('sendLiveModuleViewEvent', params)
  }

  async sendBannerImpressionEvent(params: SendEventParams.BannerImpression) {
    return this.send('sendBannerImpressionEvent', params)
  }

  async sendBannerClickEvent(params: SendEventParams.BannerClick) {
    return this.send('sendBannerClickEvent', params)
  }

  async sendBannerDismissEvent(params: SendEventParams.BannerDismiss) {
    return this.send('sendBannerDismissEvent', params)
  }

  async sendSignUpEvent(params: SendEventParams.SignUp) {
    return this.send('sendSignUpEvent', params)
  }
}
